'use client';

import React, { useState, useRef, useCallback } from 'react';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import { canvasPreview } from './canvasPreview';
import { useDebounceEffect } from './useDebounceEffect';
import useLocalizedCopy from '../../hooks/useLocalizedCopy';

import 'react-image-crop/dist/ReactCrop.css';

function Avatar({ src, onClick }: { src: string; onClick?: () => void }) {
    return (
        <div onClick={onClick} className="cursor-pointer">
            <img
                src={src}
                className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-24"
                alt="avatar"
            />
        </div>
    );
}

export default function ModalFuneralHomeLogoCropper({
    setPhotoBlob,
    currentPhotoUrl = undefined,
}: {
    setPhotoBlob: (blob: Blob) => void;
    currentPhotoUrl?: string;
}) {
    const { copy } = useLocalizedCopy();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [imgSrc, setImgSrc] = useState('');
    const [cropDialogIsOpen, setCropDialogIsOpen] = useState(false);

    const previewCanvasRef = useRef<HTMLCanvasElement>(null);
    const imgRef = useRef<HTMLImageElement>(null);

    const [crop, setCrop] = useState<Crop>();
    const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
    const [scale, setScale] = useState(1);
    const [rotate, setRotate] = useState(0);

    /* ---------- helpers ---------- */

    function onSelectFile(e: React.ChangeEvent<HTMLInputElement>) {
        if (e.target.files?.length) {
            setCrop(undefined); // reset previous crop
            const reader = new FileReader();
            reader.onload = () => {
                setImgSrc(reader.result?.toString() || '');
                setCropDialogIsOpen(true);
            };
            reader.readAsDataURL(e.target.files[0]);
        }
    }
    /** When the image finishes loading, make the crop the *entire* image. */
    function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
        const img = e.currentTarget;

        // 100 % × 100 % with the top-left corner at (0, 0)
        setCrop({
            unit: '%',  // percentages are relative to the image’s own size
            x: 0,
            y: 0,
            width: 100,
            height: 100,
        });
    }

    const onSetPhoto = useCallback(async () => {
        const image = imgRef.current;
        const previewCanvas = previewCanvasRef.current;
        if (!image || !previewCanvas || !completedCrop) return;

        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;

        const offscreen = new OffscreenCanvas(
            completedCrop.width * scaleX,
            completedCrop.height * scaleY
        );
        const ctx = offscreen.getContext('2d')!;
        ctx.drawImage(
            previewCanvas,
            0,
            0,
            previewCanvas.width,
            previewCanvas.height,
            0,
            0,
            offscreen.width,
            offscreen.height
        );

        const blob = await offscreen.convertToBlob({ type: 'image/png' });
        setPhotoBlob(blob);
    }, [completedCrop, setPhotoBlob]);

    useDebounceEffect(
        () => {
            if (
                completedCrop?.width &&
                completedCrop?.height &&
                imgRef.current &&
                previewCanvasRef.current
            ) {
                canvasPreview(
                    imgRef.current,
                    previewCanvasRef.current,
                    completedCrop,
                    scale,
                    rotate
                );
            }
        },
        100,
        [completedCrop, scale, rotate]
    );

    const onCancel = () => {
        setCrop(undefined);
        setCompletedCrop(undefined);
        setImgSrc('');
        setCropDialogIsOpen(false);
        fileInputRef.current!.value = '';
    };

    /* ---------- render ---------- */

    return (
        <div>
            {cropDialogIsOpen && (
                <>
                    {/* modal backdrop */}
                    <div className="opacity-25 fixed inset-0 z-40 bg-black" />
                    {/* modal */}
                    <div className="fixed inset-0 z-50 flex items-center justify-center">
                        <div className="bg-white rounded-lg shadow-lg max-w-3xl w-full">
                            {/* header */}
                            <div className="flex justify-between p-5 border-b">
                                <h3 className="text-xl font-semibold">{copy.Common.buttons.edit} {copy.Account.details.logo}</h3>
                            </div>
                            {/* body */}
                            <div className="p-6 max-h-[50vh] overflow-auto">
                                {(imgSrc || currentPhotoUrl) && (
                                    <ReactCrop
                                        crop={crop}
                                        onChange={(_, p) => setCrop(p)}
                                        onComplete={(c) => setCompletedCrop(c)}
                                        minHeight={100}
                                    >
                                        {/* eslint-disable-next-line @next/next/no-img-element */}
                                        <img
                                            ref={imgRef}
                                            alt="Select crop"
                                            src={imgSrc || currentPhotoUrl}
                                            style={{ transform: `scale(${scale}) rotate(${rotate}deg)` }}
                                            crossOrigin="anonymous"
                                            onLoad={onImageLoad}
                                        />
                                    </ReactCrop>
                                )}
                            </div>
                            {/* footer */}
                            <div className="flex justify-end p-6 border-t">
                                <button className="btn btn-sm btn-light me-2" onClick={onCancel}>
                                    {copy.Common.buttons.cancel}
                                </button>
                                <button
                                    className="btn btn-sm btn-primary"
                                    onClick={() => {
                                        setCropDialogIsOpen(false);
                                        onSetPhoto();
                                    }}
                                >
                                    {copy.Common.buttons.save}
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* existing / preview image */}
            {completedCrop ? (
                <canvas
                    ref={previewCanvasRef}
                    className="mt-4 border border-black w-24 h-24 rounded-full object-contain cursor-pointer"
                    onClick={() => setCropDialogIsOpen(true)}
                />
            ) : (
                currentPhotoUrl && (
                    <div className="mt-4">
                        <Avatar
                            src={currentPhotoUrl}
                            onClick={() => setCropDialogIsOpen(true)}
                        />
                    </div>
                )
            )}

            {/* controls */}
            {!imgSrc && (
                <div className="mt-4">
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={onSelectFile}
                        className="text-sm"
                    />
                </div>
            )}
        </div>
    );
}
